import { useCallback, useEffect, useMemo } from "react";

import { uiAmountToLamports, lamportsToUiAmount, Result, roundDownToDecimals } from "@bridgesplit/utils";
import BN from "bn.js";
import {
    BsMetaUtil,
    getRaydiumPositionYield,
    getRaydiumTokenAmountsFromPoolData,
    parseRaydiumAmounts,
    useUserAvailableAssets
} from "@bridgesplit/abf-react";
import { TokenPositionExpanded } from "@bridgesplit/abf-sdk";

import { AUTO_SLIPPAGE_DECIMALS, useRaydiumContext } from "../RaydiumContext";
import { getOpenPositionQuote } from "../raydium";
import { calculateRaydiumDepositRatios, getTokenUsdPrice, useRaydiumLtv } from "../util";

export function useRaydiumOpenPositionHooks() {
    useMaintainOpenPositionQuote();
    useInitializeOpenPositionPriceRange();
    useInitializeOpenPositionAmounts();
}

function useMaintainOpenPositionQuote() {
    const {
        raydiumPosition,
        poolData,
        raydiumOffchain,
        openPositionForm,
        setOpenPositionForm,
        setOpenPositionQuote,
        slippageController: { slippagePercentDecimals }
    } = useRaydiumContext();

    const fetchYieldData = useCallback(async () => {
        try {
            if (!poolData || !raydiumOffchain) return;

            if (openPositionForm.tickLower === undefined || openPositionForm.tickUpper === undefined) return;

            const { tokenA, tokenB } = raydiumPosition;

            const side = (() => {
                if (openPositionForm.side) return openPositionForm.side;

                // pick the side that has more balance if side is not set
                const percentOfAMax = (openPositionForm.tokenAAmount ?? 0) / tokenA.amount;
                const percentOfBMax = (openPositionForm.tokenBAmount ?? 0) / tokenB.amount;
                return percentOfAMax > percentOfBMax ? "A" : "B";
            })();
            const token = side === "A" ? tokenA : tokenB;
            const amount = side === "A" ? openPositionForm.tokenAAmount : openPositionForm.tokenBAmount;
            if (amount === undefined) return;

            if (openPositionForm.tickUpper < openPositionForm.tickLower) {
                return setOpenPositionForm((prev) => ({ ...prev, status: "error" }));
            }

            setOpenPositionForm((prev) => ({
                ...prev,
                status: prev.status === "silent-refetch" ? prev.status : "refetching"
            }));

            const quote = getOpenPositionQuote({
                position: raydiumPosition,
                poolData,
                tokenMint: token.mint,
                tokenAmount: new BN(uiAmountToLamports(amount, token.decimals)),
                tickLowerIndex: openPositionForm.tickLower,
                tickUpperIndex: openPositionForm.tickUpper,
                slippageTolerance: slippagePercentDecimals ?? AUTO_SLIPPAGE_DECIMALS
            });

            const tokenAAmount = lamportsToUiAmount(quote.estTokenA.toNumber(), tokenA.decimals);
            const tokenBAmount = lamportsToUiAmount(quote.estTokenB.toNumber(), tokenB.decimals);
            setOpenPositionForm((prev) => ({
                ...prev,
                tokenAAmount: side === "A" ? prev.tokenAAmount : tokenAAmount,
                tokenBAmount: side === "B" ? prev.tokenBAmount : tokenBAmount,
                maxTokenAAmount: lamportsToUiAmount(quote.maxTokenA.toNumber(), tokenA.decimals),
                maxTokenBAmount: lamportsToUiAmount(quote.maxTokenB.toNumber(), tokenB.decimals),
                status: "updated"
            }));
            setOpenPositionQuote(quote);
        } catch (error) {
            Result.err(error);
            setOpenPositionForm((prev) => ({ ...prev, status: "error" }));
        }
    }, [
        poolData,
        raydiumOffchain,
        openPositionForm.tickLower,
        openPositionForm.tickUpper,
        openPositionForm.tokenAAmount,
        openPositionForm.tokenBAmount,
        openPositionForm.side,
        raydiumPosition,
        setOpenPositionForm,
        slippagePercentDecimals,
        setOpenPositionQuote
    ]);

    useEffect(() => {
        if (openPositionForm.status && ["refetch-needed", "silent-refetch"].includes(openPositionForm.status)) {
            fetchYieldData();
        }
    }, [fetchYieldData, openPositionForm.status]);

    return { fetchYieldData };
}

function useInitializeOpenPositionAmounts() {
    const { raydiumPosition, openPositionForm, setOpenPositionForm } = useRaydiumContext();
    const { balances } = useRaydiumOpenPositionBalances();
    useEffect(() => {
        if (!raydiumPosition || openPositionForm.amountsInitialized) return;
        setOpenPositionForm((prev) => ({
            ...prev,
            // might be less than starting balance if user's wallet can't cover slippage
            tokenAAmount: raydiumPosition.tokenA.amount,
            tokenBAmount: raydiumPosition.tokenB.amount,
            amountsInitialized: true
        }));
    }, [openPositionForm.amountsInitialized, setOpenPositionForm, raydiumPosition, balances]);
}

function useInitializeOpenPositionPriceRange() {
    const { setOpenPositionForm, positionData, openPositionForm } = useRaydiumContext();

    useEffect(() => {
        if (!positionData || openPositionForm.pricesInitialized) return;
        setOpenPositionForm((prev) => ({
            ...prev,
            tickLower: positionData?.tickLower,
            tickUpper: positionData?.tickUpper,
            pricesInitialized: true
        }));
    }, [positionData, openPositionForm.pricesInitialized, setOpenPositionForm]);
}

export function useOpenPositionDepositRatio() {
    const { raydiumPosition, poolData, openPositionForm } = useRaydiumContext();
    return useMemo(() => {
        if (!poolData || openPositionForm.tickLower === undefined || openPositionForm.tickUpper === undefined)
            return undefined;

        return calculateRaydiumDepositRatios(
            poolData.sqrtPriceX64,
            raydiumPosition.tokenA.metadata.decimals,
            raydiumPosition.tokenB.metadata.decimals,
            openPositionForm.tickLower,
            openPositionForm.tickUpper
        );
    }, [
        openPositionForm.tickLower,
        openPositionForm.tickUpper,
        poolData,
        raydiumPosition.tokenA.metadata.decimals,
        raydiumPosition.tokenB.metadata.decimals
    ]);
}

export function useOpenPositionYieldData() {
    const { raydiumPosition, poolData, raydiumOffchain, openPositionQuote, positionData } = useRaydiumContext();
    const usdValue = useRaydiumUsdValue();
    return useMemo(() => {
        if (!poolData || !raydiumOffchain || !usdValue || !positionData) return undefined;

        const liquidity = openPositionQuote?.liquidity ?? raydiumPosition.position.liquidity;

        const { tokenAUsd, tokenBUsd, positionUsdValue } = usdValue;

        const positionLiquidity = tokenAUsd === undefined || tokenBUsd === undefined ? new BN(0) : liquidity;

        const poolLiquidity = poolData.liquidity;

        const yieldData = getRaydiumPositionYield({
            // set liquidity to 0 if either token is 0
            positionLiquidity,
            // add position liquidity and subtract previous liquidity
            poolLiquidity: poolLiquidity.add(positionLiquidity).sub(positionData.liquidity),
            raydiumOffchain,
            positionUsdValue
        });
        return yieldData;
    }, [
        poolData,
        raydiumOffchain,
        usdValue,
        positionData,
        openPositionQuote?.liquidity,
        raydiumPosition.position.liquidity
    ]);
}

function useRaydiumUsdValue() {
    const { raydiumPosition, poolData, raydiumOffchain, openPositionQuote } = useRaydiumContext();
    return useMemo(() => {
        if (!poolData || !raydiumOffchain) return undefined;
        const { sqrtPriceX64 } = poolData;

        const shouldUseQuote = openPositionQuote !== undefined;

        const liquidityAmounts = getRaydiumTokenAmountsFromPoolData(raydiumPosition, sqrtPriceX64);

        const tokenAAmount = shouldUseQuote
            ? openPositionQuote.estTokenA
            : liquidityAmounts[raydiumPosition.tokenA.mint];
        const tokenBAmount = shouldUseQuote
            ? openPositionQuote.estTokenB
            : liquidityAmounts[raydiumPosition.tokenB.mint];

        const tokenAUsd = getTokenUsdPrice(raydiumPosition.tokenA, new BN(tokenAAmount));
        const tokenBUsd = getTokenUsdPrice(raydiumPosition.tokenB, new BN(tokenBAmount));

        const positionUsdValue = tokenAUsd + tokenBUsd;
        return { positionUsdValue, tokenAUsd, tokenBUsd };
    }, [poolData, raydiumOffchain, openPositionQuote, raydiumPosition]);
}

export function useRaydiumOpenPositionBalances() {
    const {
        openPositionForm,
        initialWithdrawQuote,
        raydiumPosition,
        slippageController: { slippagePercentDecimals }
    } = useRaydiumContext();

    const { data } = useUserAvailableAssets({});

    const feeAmounts = parseRaydiumAmounts(raydiumPosition);

    const getBalance = useCallback(
        (balance: BN | undefined, token: TokenPositionExpanded) => {
            if (!data || !balance) return undefined;

            const userBalance = data.find((d) => d.metadata.mint === token.mint)?.amount ?? 0;
            const fee = feeAmounts.find((a) => a.mint === token.mint)?.uiAmount ?? 0;

            // need to manually add withdraw slippage
            const positionPreSlippage = lamportsToUiAmount(balance.toNumber(), token.decimals);
            const position = roundDownToDecimals(
                positionPreSlippage * (1 - (slippagePercentDecimals ?? AUTO_SLIPPAGE_DECIMALS)),
                token.decimals
            );

            const total = roundDownToDecimals(userBalance + position + fee, token.decimals);
            const totalUsd = total * token.usdPrice;

            return { total, userBalance, fee, position, totalUsd };
        },
        [data, feeAmounts, slippagePercentDecimals]
    );

    const balances = useMemo(() => {
        const A = getBalance(initialWithdrawQuote?.estTokenA, raydiumPosition.tokenA);
        const B = getBalance(initialWithdrawQuote?.estTokenB, raydiumPosition.tokenB);

        return { A, B };
    }, [
        getBalance,
        initialWithdrawQuote?.estTokenA,
        initialWithdrawQuote?.estTokenB,
        raydiumPosition.tokenA,
        raydiumPosition.tokenB
    ]);

    // Since Orca passes the amount user inputs + slippage, we need to subtract the slippage to get the actual balance as max

    const insufficientBalanceMessage = useMemo(() => {
        if ((balances.A?.total ?? 0) < (openPositionForm.tokenAAmount ?? 0))
            return `Insufficient ${BsMetaUtil.getSymbol(raydiumPosition.tokenA.metadata)} balance`;
        if ((balances.B?.total ?? 0) < (openPositionForm.tokenBAmount ?? 0))
            return `Insufficient ${BsMetaUtil.getSymbol(raydiumPosition.tokenB.metadata)} balance`;
    }, [
        openPositionForm.tokenAAmount,
        openPositionForm.tokenBAmount,
        raydiumPosition.tokenA.metadata,
        raydiumPosition.tokenB.metadata,
        balances
    ]);

    return { insufficientBalanceMessage, balances };
}

export function useRaydiumOpenPositionLtv() {
    const rp = useRaydiumUsdValue();

    return useRaydiumLtv(rp?.positionUsdValue);
}
